import { useEffect, useRef } from "react";
import { useInterventionsContext } from "@/contexts/InterventionsContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { oids } from "@/configs/Settings";
import { needsSncrNumero } from "../helpers/receituarioProfile";
import { ensureSncrPreviewForIntervention } from "../services/ensureSncrNumeroForIntervention";

export function useSncrPreviewOnSpecials(enabled = true): void {
  const { selectedInterventions, setInterventionsList } =
    useInterventionsContext();
  const { user } = useSessionContext();
  const runningRef = useRef(false);

  const list = selectedInterventions?.all || [];

  const pendingKey = list
    .map((el: any, index: number) => {
      if (el?.sign || !needsSncrNumero(el)) return "";
      if (el?.sncrNumero && el?.sncrBasicId && !el?.sncrClaimed) return "";
      if (el?.sncrClaimed) return "";
      return [
        index,
        el?.sncrTipo || "",
        el?.receituarioModelo || "",
        el?.name || el?.label || "",
      ].join(":");
    })
    .filter(Boolean)
    .join("|");

  useEffect(() => {
    if (!enabled || !pendingKey || runningRef.current) return;

    const practitionerReference = user?.username
      ? `Practitioner/${oids.cpf}-${user.username}`
      : undefined;

    let cancelled = false;

    (async () => {
      runningRef.current = true;
      try {
        const current = [...(selectedInterventions?.all || [])];
        const usedByOthers = new Set<string>();
        let changed = false;
        const next = [...current];

        for (const item of next) {
          if (
            !item?.sign &&
            needsSncrNumero(item) &&
            item?.sncrNumero &&
            !item?.sncrClaimed
          ) {
            usedByOthers.add(item.sncrNumero);
          }
        }

        for (let i = 0; i < next.length; i++) {
          const item = next[i];
          if (item?.sign || !needsSncrNumero(item)) continue;
          if (item?.sncrClaimed) continue;
          if (item?.sncrNumero && item?.sncrBasicId) {
            usedByOthers.add(item.sncrNumero);
            continue;
          }

          const exclude = [...usedByOthers];
          try {
            const stamped = await ensureSncrPreviewForIntervention(
              item,
              { practitionerReference },
              exclude,
            );
            if (
              stamped?.sncrNumero !== item?.sncrNumero ||
              stamped?.sncrBasicId !== item?.sncrBasicId
            ) {
              next[i] = stamped;
              changed = true;
            }
            if (stamped?.sncrNumero) {
              usedByOthers.add(stamped.sncrNumero);
            }
          } catch (err) {
            console.warn("[SNCR preview]", err);
          }
        }

        if (!cancelled && changed) {
          setInterventionsList(next);
        }
      } finally {
        runningRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, pendingKey, user?.username, setInterventionsList]);
}
